import React, { useEffect, useState } from 'react';
import { User, Mail, Phone, Edit2, LogOut, QrCode, History, Crown, Save, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { customerAPI } from '../../services/api';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [qrData, setQrData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        phone: user.phone || '',
      });
      fetchQR();
    }
  }, [user]);

  const fetchQR = async () => {
    try {
      const response = await customerAPI.getQR(user.id);
      setQrData(response.data);
    } catch (error) {
      console.error('Failed to fetch QR:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await customerAPI.update(user.id, form);
      updateUser(response.data);
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getTierColor = (tier) => ({
    Bronze: 'tier-bronze',
    Silver: 'tier-silver',
    Gold: 'tier-gold',
    Platinum: 'tier-platinum',
  })[tier] || 'tier-bronze';

  const tierBenefits = {
    Bronze: ['Earn 10 pts per $1', 'Birthday bonus 50 pts', 'Member-only offers'],
    Silver: ['Earn 12.5 pts per $1', 'Birthday bonus 75 pts', 'Priority booking', '5% off products'],
    Gold: ['Earn 15 pts per $1', 'Birthday bonus 100 pts', 'VIP booking', '10% off products', 'Free blow dry monthly'],
    Platinum: ['Earn 20 pts per $1', 'Birthday bonus 200 pts', 'Concierge service', '20% off products', 'Free service monthly', 'Exclusive events'],
  };

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B] mb-2">Profile</h1>
        <p className="text-gray-600">Manage your account and view your QR code</p>
      </div>

      {/* QR Code Card */}
      <div className="glass rounded-2xl p-6 text-center" data-testid="profile-qr-section">
        <p className="text-gray-600 mb-4">Show this QR to staff when visiting</p>
        
        {qrData ? (
          <div className="qr-container mx-auto inline-block">
            <QRCode
              value={JSON.stringify(qrData)}
              size={250}
              level="H"
              fgColor="#4A154B"
            />
          </div>
        ) : (
          <div className="w-64 h-64 bg-gray-100 rounded-xl mx-auto flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}
        
        <div className="mt-4">
          <span className={`inline-block px-4 py-2 rounded-full font-semibold ${getTierColor(user?.tier)}`}>
            <Crown className="w-4 h-4 inline mr-1" />
            {user?.tier || 'Bronze'} Member
          </span>
        </div>
        
        <p className="text-sm text-gray-500 mt-3">QR refreshes every 5 minutes for security</p>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchQR}
          className="mt-2"
          data-testid="refresh-qr-btn"
        >
          <QrCode className="w-4 h-4 mr-1" /> Refresh QR
        </Button>
      </div>

      {/* Profile Info */}
      <div className="glass rounded-2xl p-6" data-testid="profile-info-section">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl text-[#4A154B]">Personal Information</h3>
          {!editing ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-profile-btn"
            >
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary py-2"
                data-testid="save-profile-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <User className="w-4 h-4 text-[#C9A689]" /> Name
            </label>
            {editing ? (
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                data-testid="name-input"
              />
            ) : (
              <p className="text-[#4A154B] font-medium">{user?.name || 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="form-label flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#C9A689]" /> Email
            </label>
            <p className="text-[#4A154B] font-medium">{user?.email}</p>
          </div>

          <div>
            <label className="form-label flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#C9A689]" /> Phone
            </label>
            {editing ? (
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Your phone number"
                data-testid="phone-input"
              />
            ) : (
              <p className="text-[#4A154B] font-medium">{user?.phone || 'Not set'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tier Benefits */}
      <div className="glass rounded-2xl p-6" data-testid="tier-benefits-section">
        <h3 className="font-serif text-xl text-[#4A154B] mb-4">Your {user?.tier || 'Bronze'} Benefits</h3>
        <ul className="space-y-3">
          {(tierBenefits[user?.tier] || tierBenefits.Bronze).map((benefit, index) => (
            <li key={index} className="flex items-center gap-3 text-gray-700">
              <div className="w-2 h-2 rounded-full bg-[#C9A689]" />
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/customer/history')}
          className="glass rounded-xl p-4 text-left card-hover"
          data-testid="view-history-btn"
        >
          <History className="w-6 h-6 text-[#C9A689] mb-2" />
          <span className="font-medium text-[#4A154B]">Points History</span>
        </button>
        
        <button
          onClick={handleLogout}
          className="glass rounded-xl p-4 text-left card-hover border-red-100"
          data-testid="logout-btn"
        >
          <LogOut className="w-6 h-6 text-[#C74545] mb-2" />
          <span className="font-medium text-[#C74545]">Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default Profile;
